<template>
  <!-- Mandatory: it only closes once answered, sent offline, lost or out of time. -->
  <vs-modal v-model="show" buttonless :closable="canClose" @close="onClose">
    <template #default="{ close }">
      <div class="flex flex-col gap-5 p-1">
        <!-- Header: what this is and which bus. The shield only shows a check once the bus was checked. -->
        <div class="flex items-center gap-3">
          <span class="grid h-10 w-10 flex-shrink-0 place-items-center rounded-full" :class="headerTone">
            <vs-icon :name="headerIcon" :size="20" />
          </span>
          <div class="min-w-0 flex-1">
            <h3 class="whitespace-nowrap text-lg font-semibold leading-tight text-on-surface">Revisión del bus</h3>
            <p v-if="subtitle" class="truncate text-sm text-on-surface-variant">{{ subtitle }}</p>
          </div>
        </div>
        <div
          v-if="isOpen"
          class="-mt-2 flex items-center justify-between rounded-xl px-4 py-2.5"
          :class="urgent ? 'bg-error-container text-on-error-container' : 'bg-primary-container text-on-primary-container'"
        >
          <span class="text-sm font-semibold">Tiempo para revisar</span>
          <span class="text-xl font-semibold tabular-nums" aria-hidden="true">{{ countdown }}</span>
        </div>

        <!-- Closed without a check: the phone left the bus's zone, or the time ran out. One message, one way out. -->
        <template v-if="closedReason">
          <div class="flex flex-col gap-1">
            <p class="text-base font-semibold text-on-surface">{{ closedReason === 'lost' ? 'La revisión se cerró' : 'Se acabó el tiempo' }}</p>
            <p class="text-base text-on-surface-variant">
              {{ closedReason === 'lost'
                ? 'Saliste de la zona donde terminó el recorrido, así que ya no se puede escanear.'
                : `La revisión tenía ${windowMin} minutos desde el final del recorrido.` }}
              Queda registrada como no hecha.
            </p>
          </div>
          <vs-button kind="primary" class="h-12 w-full" text="Entendido" @click="close" />
        </template>

        <!-- "Corregir": the sticker is on another bus; it moves there for every later check. -->
        <template v-else-if="view === 'done' && correcting">
          <BusWalkthroughVehiclePicker :vehicles="fleet" :current-id="checkedVehicle?.id ?? null" @pick="correctVehicle" @cancel="correcting = false; message = null" />
          <div v-if="message" class="flex items-start gap-2 rounded-lg bg-error-container p-3 text-base text-on-error-container" role="alert">
            <vs-icon name="lucide:circle-alert" :size="20" color="on-error-container" class="mt-0.5 flex-shrink-0" />
            {{ message }}
          </div>
        </template>

        <!-- Scanned: a receipt, not a thank-you. -->
        <template v-else-if="view === 'done' && doneStatus === 'done'">
          <div class="flex flex-col gap-3">
            <p class="text-base font-semibold text-on-surface">Bus revisado</p>
            <dl class="divide-y divide-surface-variant rounded-lg bg-surface-variant/40 text-base">
              <div class="flex justify-between gap-3 px-3 py-2.5">
                <dt class="text-on-surface-variant">Hora</dt>
                <dd class="font-semibold tabular-nums text-on-surface">{{ checkedAtLabel }}</dd>
              </div>
              <div v-if="doneVehicleName" class="flex items-center justify-between gap-3 px-3 py-2.5">
                <dt class="text-on-surface-variant">Bus</dt>
                <dd class="flex items-center gap-3 font-semibold text-on-surface">
                  {{ doneVehicleName }}
                  <button v-if="canCorrect" type="button" class="px-1 text-base font-semibold text-primary underline-offset-4 active:underline" @click="correcting = true; message = null">
                    Corregir
                  </button>
                </dd>
              </div>
              <div class="flex items-center gap-2 px-3 py-2.5 text-on-surface">
                <vs-icon name="lucide:bell-ring" :size="18" color="primary" />
                Avisamos a los acudientes que el bus quedó revisado.
              </div>
            </dl>
          </div>
          <vs-button kind="primary" class="h-12 w-full" text="Listo" @click="close" />
        </template>

        <!-- Skipped: recorded as such, never dressed as a check. -->
        <template v-else-if="view === 'done'">
          <div class="flex flex-col gap-1">
            <p class="text-base font-semibold text-on-surface">Registrada sin escanear</p>
            <p class="text-base text-on-surface-variant">Guardamos tu confirmación y el motivo. Tu coordinador la ve en sus alertas.</p>
          </div>
          <vs-button kind="primary" class="h-12 w-full" text="Listo" @click="close" />
        </template>

        <template v-else-if="view === 'queued'">
          <div class="flex items-start gap-3 rounded-lg bg-surface-variant/40 p-3">
            <vs-icon name="lucide:wifi-off" :size="20" color="on-surface" class="mt-0.5 flex-shrink-0" />
            <div>
              <p class="text-base font-semibold text-on-surface">Guardada sin conexión</p>
              <p class="text-base text-on-surface-variant">Se envía sola cuando vuelvas a tener señal. La hora ya quedó guardada.</p>
            </div>
          </div>
          <vs-button kind="primary" class="h-12 w-full" text="Listo" @click="close" />
        </template>

        <template v-else-if="view === 'sending'">
          <div class="flex items-center gap-3 py-4" role="status">
            <vs-preloader :size="28" />
            <p class="text-base text-on-surface">{{ sendingPhase === 'locating' ? 'Obteniendo tu ubicación…' : 'Enviando…' }}</p>
          </div>
        </template>

        <!-- A sticker's first scan: which bus is it on? Saved with that bus for every later check. -->
        <template v-else-if="view === 'confirmVehicle'">
          <BusWalkthroughVehiclePicker v-if="picking" :vehicles="fleet" @pick="pickVehicle" @cancel="backFromPicker" />
          <template v-else>
            <div class="flex flex-col gap-1">
              <p class="text-base text-on-surface">¿Este código está en el bus <strong>{{ proposedVehicle?.name }}</strong>?</p>
              <p class="text-sm text-on-surface-variant">Es la primera vez que se escanea. Queda guardado con ese bus.</p>
            </div>
            <div class="flex flex-col gap-2">
              <vs-button kind="primary" class="h-12 w-full" :text="`Sí, es ${proposedVehicle?.name || 'ese bus'}`" @click="proposedVehicle && pickVehicle(proposedVehicle)" />
              <vs-button kind="surface" class="h-12 w-full" text="Es otro bus" @click="picking = true" />
            </div>
          </template>
          <div v-if="message" class="flex items-start gap-2 rounded-lg bg-error-container p-3 text-base text-on-error-container" role="alert">
            <vs-icon name="lucide:circle-alert" :size="20" color="on-error-container" class="mt-0.5 flex-shrink-0" />
            {{ message }}
          </div>
        </template>

        <!-- Skip: one of two reasons, then the driver signs the statement by typing the word. -->
        <template v-else-if="view === 'skip'">
          <div v-if="!skipReason" class="flex flex-col gap-2">
            <p class="text-base font-semibold text-on-surface">¿Por qué no puedes escanear?</p>
            <button
              v-for="option in skipOptions"
              :key="option.id"
              type="button"
              class="flex w-full items-center gap-3 rounded-xl bg-surface p-4 text-left shadow-[0_1px_2px_rgb(0_0_0/0.06),0_4px_14px_-4px_rgb(0_0_0/0.16)] transition-[transform,box-shadow] duration-150 active:scale-[0.99] active:shadow-[0_1px_2px_rgb(0_0_0/0.08)] focus-visible:outline-2 focus-visible:outline-primary"
              @click="pickReason(option.id)"
            >
              <vs-icon :name="option.icon" :size="22" color="primary" class="flex-shrink-0" />
              <span class="min-w-0 flex-1">
                <span class="block text-base font-semibold text-on-surface">{{ option.title }}</span>
                <span class="block text-sm text-on-surface-variant">{{ option.hint }}</span>
              </span>
              <vs-icon name="lucide:chevron-right" :size="18" color="on-surface" class="opacity-60" />
            </button>
          </div>

          <template v-else>
            <p class="text-sm text-on-surface-variant">{{ skipOptions.find(o => o.id === skipReason)?.title }}</p>
            <div v-if="skipReason === 'camera' && cameraProblem" class="flex flex-col gap-3 rounded-xl bg-surface-variant/60 p-4">
              <p class="flex items-start gap-2 text-base font-semibold text-on-surface">
                <vs-icon name="lucide:camera-off" :size="20" color="on-surface" class="mt-0.5 flex-shrink-0" />
                {{ cameraProblem }}
              </p>
              <template v-if="cameraDenied">
                <vs-button kind="primary" class="h-12 w-full" iconName="lucide:settings" text="Dar permiso en Ajustes" @click="openCameraSettings()" />
                <p class="text-sm text-on-surface-variant">Activa la cámara, vuelve a la app y toca «Volver» para escanear. Si no puedes, confirma abajo.</p>
              </template>
            </div>
            <blockquote class="rounded-xl bg-primary-container p-4 text-lg font-semibold leading-snug text-on-primary-container">
              Confirmo que revisé todo el bus y no quedó nadie a bordo.
            </blockquote>
            <!-- Plain inputs: the modal owns their values (vsInput keeps its own form state, which reset the word). -->
            <div class="flex flex-col gap-4">
              <label v-if="skipReason === 'noSticker'" class="flex flex-col gap-1.5">
                <span class="text-sm font-semibold text-on-surface">Placa del bus</span>
                <input
                  v-model="plate"
                  class="h-12 w-full rounded-xl border border-outline/50 bg-surface px-4 text-lg font-semibold uppercase tracking-wide text-on-surface outline-none placeholder:normal-case focus:border-primary focus:ring-1 focus:ring-primary"
                  placeholder="ABC123"
                  autocapitalize="characters"
                  autocorrect="off"
                  spellcheck="false"
                  maxlength="8"
                />
              </label>
              <label class="flex flex-col gap-1.5">
                <span class="text-sm font-semibold text-on-surface">Escribe «{{ acceptWord }}» para confirmar</span>
                <input
                  v-model="acceptText"
                  class="h-12 w-full rounded-xl border border-outline/50 bg-surface px-4 text-lg text-on-surface outline-none focus:border-primary focus:ring-1 focus:ring-primary"
                  :placeholder="acceptWord"
                  autocapitalize="none"
                  autocorrect="off"
                  autocomplete="off"
                  spellcheck="false"
                />
              </label>
            </div>
          </template>

          <div v-if="message" class="flex items-start gap-2 rounded-lg bg-error-container p-3 text-base text-on-error-container" role="alert">
            <vs-icon name="lucide:circle-alert" :size="20" color="on-error-container" class="mt-0.5 flex-shrink-0" />
            {{ message }}
          </div>
          <div class="flex flex-col gap-2">
            <vs-button v-if="skipReason" kind="primary" class="h-12 w-full" text="Confirmar sin escanear" :disabled="!skipReady" @click="sendSkip" />
            <vs-button kind="surface" class="h-12 w-full" text="Volver" @click="backFromSkip" />
          </div>
        </template>

        <template v-else>
          <ol class="flex flex-col gap-3 text-base text-on-surface">
            <li class="flex gap-3">
              <span class="grid h-6 w-6 flex-shrink-0 place-items-center rounded-full bg-primary-container text-sm font-semibold text-on-primary-container">1</span>
              Camina hasta el último puesto mirando cada fila.
            </li>
            <li class="flex gap-3">
              <span class="grid h-6 w-6 flex-shrink-0 place-items-center rounded-full bg-primary-container text-sm font-semibold text-on-primary-container">2</span>
              Escanea el código pegado en ese puesto.
            </li>
          </ol>
          <p class="flex items-start gap-2 text-sm text-on-surface-variant">
            <vs-icon name="lucide:bell-ring" :size="16" color="on-surface" class="mt-0.5 flex-shrink-0 opacity-70" />
            Al escanear, avisamos a los acudientes que el bus quedó revisado.
          </p>
          <div v-if="message" class="flex items-start gap-2 rounded-lg bg-error-container p-3 text-base text-on-error-container" role="alert">
            <vs-icon name="lucide:circle-alert" :size="20" color="on-error-container" class="mt-0.5 flex-shrink-0" />
            {{ message }}
          </div>
          <vs-button kind="primary" class="h-14 w-full text-lg" iconName="lucide:scan-line" text="Escanear código" @click="scan" />
          <button type="button" class="mt-2 self-center px-4 py-2 text-base font-semibold text-primary underline-offset-4 active:underline" @click="openSkip()">
            No puedo escanear
          </button>
        </template>
      </div>
    </template>
  </vs-modal>
  <QrScanOverlay fullscreen :active="scanning" hint="Escanea el código del último puesto" @cancel="stop" />
</template>

<script lang="ts">
import { computed, defineComponent, onMounted, onUnmounted, PropType, ref } from 'vue'
import { vsButton, vsIcon, vsModal, vsPreloader } from '@isagaco/vuesaga'
import { useAuthStore } from '@/stores'
import { useQrScan } from '@/composables/useQrScan'
import QrScanOverlay from '@/components/QrScanOverlay.vue'
import BusWalkthroughVehiclePicker from '@/components/BusWalkthroughVehiclePicker.vue'
import {
  ACCEPT_WORD,
  CHECK_WINDOW_MIN,
  forgetCheck,
  isAccepted,
  isPlate,
  keepCheckShown,
  lostChecks,
  normalizePlate,
  readPendingChecks,
  getCheckPosition,
  isWalkthroughCode,
  postBusWalkthrough,
  queueBusWalkthrough,
  type CheckPosition,
  type CheckZone,
  type FleetVehicle,
  type BusWalkthroughPayload,
  type BusWalkthroughRequest,
  type SkipReason,
  URGENT_MS,
} from '@/utils/busWalkthrough'

/** Offline, the pending check is read again this often, this many times (online, one read answers). */
const OFFLINE_RETRY_MS = 5000
const OFFLINE_READS = 12

const errorMessage = (error: string, distanceM?: number | null): string => {
  switch (error) {
    case 'unknownCode': return 'Ese código no es el de revisión del bus. Busca el del último puesto.'
    case 'outOfRadius': return `Estás a ${Math.round(distanceM ?? 0)} m de donde terminó el recorrido. Acércate al bus y vuelve a escanear.`
    case 'outOfWindow': return 'Se acabó el tiempo de la revisión.'
    case 'badPlate': return 'Escribe la placa completa del bus, por ejemplo ABC123.'
    case 'notAccepted': return `Escribe «${ACCEPT_WORD}» para confirmar.`
    case 'notStreamer': return 'Esta revisión le corresponde al celular que transmitió el recorrido.'
    case 'noCheck': return 'Este recorrido no tiene revisión del bus pendiente.'
    case 'notCustomerVehicle': return 'Ese bus no es de tu empresa. Elige otro.'
    case 'notCorrectable': return 'Esta revisión ya no se puede corregir.'
    default: return 'No pudimos registrar la revisión. Vuelve a intentarlo.'
  }
}

const SKIP_OPTIONS: Array<{ id: SkipReason, icon: string, title: string, hint: string }> = [
  { id: 'noSticker', icon: 'lucide:qr-code', title: 'No hay código o no se lee', hint: 'Escribes la placa del bus' },
  { id: 'camera', icon: 'lucide:camera-off', title: 'La cámara no funciona', hint: 'El celular no puede escanear' },
]

const timeLabel = (ms: number) => new Date(ms).toLocaleTimeString('es-CO', { hour: 'numeric', minute: '2-digit', hour12: true })

export default defineComponent({
  name: 'BusWalkthroughModal',
  components: { vsButton, vsIcon, vsModal, vsPreloader, QrScanOverlay, BusWalkthroughVehiclePicker },
  props: {
    request: { type: Object as PropType<BusWalkthroughRequest>, required: true },
  },
  emits: ['close'],
  setup(props, { emit }) {
    const authStore = useAuthStore()
    const show = ref(true)
    const view = ref<'main' | 'skip' | 'confirmVehicle' | 'sending' | 'done' | 'queued'>('main')
    const sendingPhase = ref<'locating' | 'sending'>('sending')
    const message = ref<string | null>(null)
    const doneStatus = ref<'done' | 'skipped'>('done')
    const checkedAt = ref<number>(Date.now())
    const skipReason = ref<SkipReason | null>(null)
    const plate = ref('')
    const acceptText = ref('')
    const skipReady = computed(() => !!skipReason.value && isAccepted(acceptText.value) &&
      (skipReason.value !== 'noSticker' || isPlate(plate.value)))
    // A sticker's first scan (409 confirmVehicle): the scan waiting for its bus, the bus proposed, the fleet to pick from.
    let pendingScan: BusWalkthroughPayload | null = null
    const proposedVehicle = ref<FleetVehicle | null>(null)
    const picking = ref(false)
    const fleet = ref<FleetVehicle[]>([])
    // A unique sticker's bus once done (the server's answer), and "Corregir" while the check is still open.
    const checkedVehicle = ref<FleetVehicle | null>(null)
    const correcting = ref(false)

    const routeName = ref(props.request.routeName || null)
    const vehicleName = ref(props.request.vehicleName || null)
    const doneVehicleName = computed(() => checkedVehicle.value?.name || vehicleName.value)
    const subtitle = computed(() => [routeName.value, vehicleName.value].filter(Boolean).join(' · '))

    // Countdown: the pending row's dueAt when the server has it, else the window counted from the opening.
    const dueAt = ref<number>(props.request.dueAt ? Date.parse(props.request.dueAt) : Date.now() + CHECK_WINDOW_MIN * 60000)
    const now = ref(Date.now())
    const tick = window.setInterval(() => { now.value = Date.now() }, 1000)
    const remainingMs = computed(() => dueAt.value - now.value)
    const urgent = computed(() => remainingMs.value <= URGENT_MS)
    const countdown = computed(() => {
      const total = Math.max(0, Math.ceil(remainingMs.value / 1000))
      return `${Math.floor(total / 60)}:${String(total % 60).padStart(2, '0')}`
    })
    // The server's row: its deadline, names and zones. It exists by the time the trip end reaches the phone, so one
    // answer settles it; offline it is read again until the server answers. No row: nothing to check (a voided false
    // start, an answered check, the flag off): the modal closes from any step, a check that doesn't exist never traps.
    const loadPending = async () => {
      for (let i = 0; i < OFFLINE_READS; i++) {
        const checks = await readPendingChecks()
        if (checks) {
          const check = checks.find(c => c.tripID === props.request.tripID)
          if (!check) {
            if (!['sending', 'done', 'queued'].includes(view.value) && !props.request.payload) dropCheck()
            return
          }
          if (check.dueAt) dueAt.value = Date.parse(check.dueAt)
          routeName.value = routeName.value || check.routeName || null
          vehicleName.value = vehicleName.value || check.vehicleName || null
          zones.value = check.zones
          // Into the shown checks: the position watch closes it if the phone leaves (utils/busWalkthrough.ts).
          keepCheckShown(check)
          return
        }
        await new Promise(r => setTimeout(r, OFFLINE_RETRY_MS))
      }
    }
    // The server holds no check for this phone (none for the trip, or another account streamed it): let go of it.
    const dropCheck = () => {
      if (scanning.value) stop()
      forgetCheck(props.request.tripID)
      show.value = false
    }
    const zones = ref<CheckZone[] | undefined>(undefined)
    const lost = computed(() => !!lostChecks.value[props.request.tripID])
    const settled = computed(() => view.value === 'done' || view.value === 'queued')
    // Why the check can no longer be done here (lost or out of time), or null while it still can.
    const closedReason = computed<'lost' | 'expired' | null>(() => {
      if (settled.value || view.value === 'sending' || view.value === 'confirmVehicle') return null
      if (lost.value) return 'lost'
      return remainingMs.value <= 0 ? 'expired' : null
    })
    const isOpen = computed(() => !settled.value && !closedReason.value)
    // Mandatory while it can still be done: no X, no backdrop. Answered, offline, lost or out of time: closable.
    const canClose = computed(() => view.value !== 'sending' && !isOpen.value)
    const headerIcon = computed(() => {
      if (view.value === 'done' && doneStatus.value === 'done') return 'lucide:shield-check'
      if (closedReason.value) return 'lucide:shield-off'
      return 'lucide:shield'
    })
    const headerTone = computed(() => {
      if (view.value === 'done' && doneStatus.value === 'done') return 'bg-success text-on-success'
      if (closedReason.value || (isOpen.value && urgent.value)) return 'bg-error-container text-on-error-container'
      return 'bg-primary-container text-on-primary-container'
    })
    const checkedAtLabel = computed(() => timeLabel(checkedAt.value))
    // The server takes a correction until the window after the deadline ends (handlers/busWalkthrough.ts).
    const canCorrect = computed(() => !!checkedVehicle.value && fleet.value.length > 0 &&
      now.value < dueAt.value + CHECK_WINDOW_MIN * 60000)

    // Only closable once settled (canClose), so closing never leaves an open check behind.
    const onClose = () => emit('close')

    // Warmed on open, so the GPS has a fix by the time the code is read.
    let warmPosition: Promise<CheckPosition | null> = getCheckPosition()
    const freshPosition = async (): Promise<CheckPosition | null> => {
      warmPosition = getCheckPosition()
      return warmPosition
    }

    const { scanning, error: scanError, cameraDenied, start, stop, openCameraSettings } = useQrScan()
    // Why the camera didn't open, shown on the camera reason (with the settings button when it was denied).
    const cameraProblem = ref<string | null>(null)

    const submit = async (payload: BusWalkthroughPayload) => {
      view.value = 'sending'
      sendingPhase.value = 'sending'
      message.value = null
      // The row exists before the phone hears the trip ended: a "no check" answer is final (dropCheck below).
      const result = await postBusWalkthrough(payload)
      if (result.kind === 'ok') {
        // Answered: off the phone's list now, so closing this never reopens it.
        forgetCheck(props.request.tripID)
        doneStatus.value = result.status
        checkedAt.value = Date.parse(payload.scannedAt) || Date.now()
        checkedVehicle.value = result.vehicle
        fleet.value = result.vehicles
        pendingScan = null
        view.value = 'done'
      } else if (result.kind === 'offline') {
        queueBusWalkthrough(payload)
        view.value = 'queued'
      } else if (result.kind === 'confirmVehicle') {
        askVehicle(payload, result.vehicle, result.vehicles)
      } else if (result.error === 'noCheck' || result.error === 'notStreamer') {
        dropCheck()
      } else if (result.error === 'notCustomerVehicle' && pendingScan) {
        // The picked bus isn't one of the customer's: pick again.
        message.value = errorMessage(result.error)
        picking.value = true
        view.value = 'confirmVehicle'
      } else {
        // The error shows on the step that was sent: a skip stays on its statement, a scan goes back to scanning.
        message.value = errorMessage(result.error, result.distanceM)
        view.value = payload.action === 'skip' ? 'skip' : 'main'
      }
    }

    const basePayload = (position: CheckPosition | null) => ({
      tripID: props.request.tripID,
      lat: position?.lat ?? null,
      lng: position?.lng ?? null,
      accuracy: position?.accuracy ?? null,
      scannedAt: new Date().toISOString(),
      deviceID: authStore.deviceID || null,
    })

    // The signature is typed fresh for each reason: leaving the statement clears it.
    const clearSignature = () => {
      plate.value = ''
      acceptText.value = ''
    }
    const openSkip = (reason: SkipReason | null = null) => {
      clearSignature()
      skipReason.value = reason
      message.value = null
      view.value = 'skip'
    }
    const pickReason = (reason: SkipReason) => {
      clearSignature()
      skipReason.value = reason
      message.value = null
    }
    // Back walks one step: from the statement to the reasons, from the reasons to the scan.
    const backFromSkip = () => {
      message.value = null
      clearSignature()
      if (skipReason.value) skipReason.value = null
      else {
        cameraProblem.value = null
        view.value = 'main'
      }
    }

    const scan = async () => {
      message.value = null
      const text = await start()
      if (!text) {
        // The camera failed (no permission, no camera): straight to its skip reason, with what went wrong and, when the
        // permission was denied before (the system won't ask again), the way to give it.
        if (scanError.value) {
          openSkip('camera')
          cameraProblem.value = scanError.value
        }
        return
      }
      if (!isWalkthroughCode(text)) {
        message.value = errorMessage('unknownCode')
        return
      }
      const scannedAt = new Date().toISOString()
      view.value = 'sending'
      sendingPhase.value = 'locating'
      const position = await freshPosition()
      // `bind`: this copy answers which bus a sticker read for the first time is on.
      await submit({ ...basePayload(position), scannedAt, action: 'scan', code: text, bind: true })
    }

    const sendSkip = async () => {
      if (!skipReady.value || !skipReason.value) return
      const scannedAt = new Date().toISOString()
      view.value = 'sending'
      sendingPhase.value = 'locating'
      const position = await warmPosition
      await submit({
        ...basePayload(position), scannedAt, action: 'skip', reason: skipReason.value,
        plate: skipReason.value === 'noSticker' ? normalizePlate(plate.value) : undefined,
        accept: acceptText.value.trim(),
      })
    }

    // The sticker's first scan: "¿Este código está en el bus …?" with the server's proposal, or straight to the list
    // when the server has none (no bus for the route, or one outside the customer's fleet).
    const askVehicle = (payload: BusWalkthroughPayload, vehicle: FleetVehicle | null, vehicles: FleetVehicle[]) => {
      pendingScan = payload
      proposedVehicle.value = vehicle
      fleet.value = vehicles
      picking.value = !vehicle
      message.value = null
      view.value = 'confirmVehicle'
    }
    // The same scan again (its time and position) with the bus: the server binds the sticker and marks the check done.
    const pickVehicle = (vehicle: FleetVehicle) => {
      if (!pendingScan) return
      void submit({ ...pendingScan, vehicleID: vehicle.id })
    }
    const backFromPicker = () => {
      message.value = null
      if (proposedVehicle.value) {
        picking.value = false
        return
      }
      pendingScan = null
      view.value = 'main'
    }

    // "Corregir": the sticker moves to the picked bus for every later check, and this check records it.
    const correctVehicle = async (vehicle: FleetVehicle) => {
      if (vehicle.id === checkedVehicle.value?.id) {
        correcting.value = false
        return
      }
      message.value = null
      const result = await postBusWalkthrough({ tripID: props.request.tripID, action: 'correctVehicle', vehicleID: vehicle.id, scannedAt: new Date().toISOString() })
      if (result.kind === 'ok') {
        checkedVehicle.value = result.vehicle ?? vehicle
        if (result.vehicles.length) fleet.value = result.vehicles
        correcting.value = false
      } else if (result.kind === 'offline') {
        message.value = 'Sin señal. Vuelve a intentarlo cuando tengas conexión.'
      } else if (result.kind === 'rejected') {
        message.value = errorMessage(result.error === 'outOfWindow' ? 'notCorrectable' : result.error)
      }
    }

    onMounted(() => {
      // A queued first scan of a sticker: ask which bus it is on now.
      if (props.request.payload && props.request.confirm) {
        askVehicle(props.request.payload, props.request.confirm.vehicle, props.request.confirm.vehicles)
      }
      // Kept on the phone from the first second: a killed app reopens it on the next start, even offline.
      else keepCheckShown({ id: props.request.tripID, tripID: props.request.tripID, dueAt: new Date(dueAt.value).toISOString(),
        routeName: routeName.value, vehicleName: vehicleName.value })
      void loadPending()
    })
    onUnmounted(() => window.clearInterval(tick))

    return {
      show, onClose, view, sendingPhase, message, doneStatus, checkedAtLabel, subtitle, doneVehicleName,
      countdown, urgent, isOpen, canClose, closedReason, headerIcon, headerTone, windowMin: CHECK_WINDOW_MIN,
      skipOptions: SKIP_OPTIONS, skipReason, plate, acceptText, acceptWord: ACCEPT_WORD, skipReady,
      scanning, stop, scan, cameraProblem, cameraDenied, openCameraSettings, openSkip, pickReason, backFromSkip, sendSkip,
      proposedVehicle, picking, fleet, pickVehicle, backFromPicker, checkedVehicle, correcting, canCorrect, correctVehicle,
    }
  },
})
</script>
